import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DashboardDataTable from 'modules/common/dashboardDataTable.component';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  formControl: {
    minWidth: 200,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  warningRow: {
    backgroundColor: theme.palette.warning.light + '30', // Yellow highlight for > 14 days
  },
  urgentRow: {
    backgroundColor: theme.palette.error.light + '30', // Red highlight for > 30 days
  },
  noExpenses: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  daysCell: {
    fontWeight: 'bold',
  },
  clickableLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  descriptionCell: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const ExpenseFollowUpGrid = ({ expenses, isLoading, clients, showExpenseAnalyst, showLineItemStatus, defaultDaysRange }) => {
  const classes = useStyles();

  // Filter state (moved internally)
  const [filters, setFilters] = useState({
    statusName: null,
    clientId: null,
    daysRange: defaultDaysRange,
  });

  const formatCurrency = (amount) => {
    if (amount == null) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getRowClassName = (daysSinceEntry) => {
    if (!daysSinceEntry) return '';
    if (daysSinceEntry > 30) return classes.urgentRow;
    if (daysSinceEntry > 14) return classes.warningRow;
    return '';
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      statusName: null,
      clientId: null,
      daysRange: null,
    });
  };

  // Extract unique statuses from expenses
  const statuses = useMemo(() => {
    const uniqueStatuses = [...new Set(expenses.map(exp => exp.statusName))].filter(Boolean);
    return uniqueStatuses.sort();
  }, [expenses]);

  // Filter expenses (sorting is handled by the grid)
  const filteredExpenses = useMemo(() => {
    let filtered = [...expenses];

    // Apply filters
    if (filters.statusName) {
      filtered = filtered.filter((exp) => exp.statusName === filters.statusName);
    }

    // TODO: Client filter disabled - API doesn't return clientId in expense response
    // Re-enable when backend adds clientId field to expense DTOs
    // See original implementation lines 99-104
    if (filters.clientId) {
      filtered = filtered.filter((exp) => {
        // Match by client name (we don't have clientId in expense response)
        // This is a workaround - ideally the API would include clientId
        return true; // For now, skip this filter until we have proper clientId
      });
    }

    if (filters.daysRange) {
      // Values are 'lt7'/'gt7' etc. — 'lt' filters to fewer days, 'gt' to more
      const isLessThan = filters.daysRange.startsWith('lt');
      const threshold = parseInt(filters.daysRange.replace(/\D/g, ''), 10);
      filtered = filtered.filter((exp) =>
        isLessThan ? exp.daysSinceEntry < threshold : exp.daysSinceEntry > threshold,
      );
    }

    return filtered;
  }, [expenses, filters]);

  const columns = [
    {
      accessorKey: 'authId',
      header: 'Auth ID',
      Cell: ({ row }) => (
        <a
          href={`/authorizations/${row.original.fileId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.clickableLink}
        >
          {row.original.authId || '-'}
        </a>
      ),
    },
    {
      accessorKey: 'eeName',
      header: 'EE Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    // Only shown on the expense manager dashboard; EA dashboard rows have no analyst values
    ...(showExpenseAnalyst
      ? [
        {
          accessorKey: 'expenseAnalystName',
          header: 'Expense Analyst',
          Cell: ({ cell }) => cell.getValue() || '-',
        },
      ]
      : []),
    {
      accessorKey: 'clientName',
      header: 'Client Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'payTo',
      header: 'Pay To',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'description',
      header: 'Description',
      enableSorting: false,
      Cell: ({ row }) => (
        <Tooltip title={row.original.description || ''} arrow>
          <a
            href={`/expenses/${row.original.expenseId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.clickableLink}
          >
            {truncateText(row.original.description, 50)}
          </a>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      Cell: ({ cell }) => formatCurrency(cell.getValue()),
    },
    {
      accessorKey: 'daysSinceEntry',
      header: 'Days Since Entry',
      muiTableBodyCellProps: { className: classes.daysCell },
      Cell: ({ cell }) => (cell.getValue() != null ? `${cell.getValue()}` : '-'),
    },
    {
      accessorKey: 'statusName',
      header: 'Status',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    // Only shown on the expense manager dashboard; the EA/RC dashboard endpoints
    // don't return line item status yet
    ...(showLineItemStatus
      ? [
        {
          accessorKey: 'lineItemStatusName',
          header: 'Line Item Status',
          Cell: ({ cell }) => cell.getValue() || '-',
        },
      ]
      : []),
  ];

  if (isLoading) {
    return (
      <Paper elevation={0} className={classes.tableContainer}>
        <Typography className={classes.noExpenses} color="textSecondary">
          Loading expenses...
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* Filter UI */}
      <Box className={classes.filtersContainer}>
        {/* Status Filter */}
        <FormControl className={classes.formControl} size="small" disabled={isLoading}>
          <InputLabel id="expense-status-filter-label">Status</InputLabel>
          <Select
            labelId="expense-status-filter-label"
            id="expense-status-filter"
            value={filters.statusName || ''}
            onChange={(e) => handleFilterChange('statusName', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All Statuses</em>
            </MenuItem>
            {statuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Client Filter - Disabled due to missing clientId in API response */}
        <FormControl className={classes.formControl} size="small" disabled>
          <InputLabel id="expense-client-filter-label">Client</InputLabel>
          <Select
            labelId="expense-client-filter-label"
            id="expense-client-filter"
            value={filters.clientId || ''}
            onChange={(e) => handleFilterChange('clientId', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All Clients (Disabled)</em>
            </MenuItem>
            {clients.map((client) => (
              <MenuItem key={client.clientId} value={client.clientId}>
                {client.clientName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Days Since Entry Range Filter */}
        <FormControl className={classes.formControl} size="small" disabled={isLoading}>
          <InputLabel id="expense-days-filter-label">Days Since Entry</InputLabel>
          <Select
            labelId="expense-days-filter-label"
            id="expense-days-filter"
            value={filters.daysRange || ''}
            onChange={(e) => handleFilterChange('daysRange', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="lt7">Less than 7 days</MenuItem>
            <MenuItem value="lt14">Less than 14 days</MenuItem>
            <MenuItem value="lt30">Less than 30 days</MenuItem>
            <MenuItem value="gt7">More than 7 days</MenuItem>
            <MenuItem value="gt14">More than 14 days</MenuItem>
            <MenuItem value="gt30">More than 30 days</MenuItem>
          </Select>
        </FormControl>

        {/* Clear Filters Button */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearFilters}
          disabled={isLoading}
        >
          Clear Filters
        </Button>
      </Box>

      {/* Grid */}
      {expenses.length === 0 ? (
        <Paper elevation={0} className={classes.tableContainer}>
          <Typography className={classes.noExpenses} color="textSecondary">
            No expenses require follow-up
          </Typography>
          <Typography className={classes.noExpenses} color="textSecondary" variant="body2">
            ✓ All expenses on your files are approved
          </Typography>
        </Paper>
      ) : filteredExpenses.length === 0 ? (
        <Paper elevation={0} className={classes.tableContainer}>
          <Typography className={classes.noExpenses} color="textSecondary">
            No expenses match the current filters
          </Typography>
        </Paper>
      ) : (
        <Box className={classes.tableContainer}>
          <DashboardDataTable
            columns={columns}
            data={filteredExpenses}
            initialSorting={[{ id: 'daysSinceEntry', desc: true }]}
            muiTableBodyRowProps={({ row }) => {
              const rowClassName = getRowClassName(row.original.daysSinceEntry);
              return rowClassName ? { className: rowClassName } : {};
            }}
          />
        </Box>
      )}
    </>
  );
};

ExpenseFollowUpGrid.propTypes = {
  expenses: PropTypes.arrayOf(PropTypes.shape({
    expenseId: PropTypes.number.isRequired,
    fileId: PropTypes.number.isRequired,
    authId: PropTypes.string.isRequired,
    eeName: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    payTo: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    entryDate: PropTypes.string.isRequired,
    daysSinceEntry: PropTypes.number.isRequired,
    statusName: PropTypes.string.isRequired,
    lineItemStatusName: PropTypes.string,
  })).isRequired,
  isLoading: PropTypes.bool,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      clientId: PropTypes.number,
      clientName: PropTypes.string,
    }),
  ),
  showExpenseAnalyst: PropTypes.bool,
  showLineItemStatus: PropTypes.bool,
  defaultDaysRange: PropTypes.oneOf(['lt7', 'lt14', 'lt30', 'gt7', 'gt14', 'gt30']),
};

ExpenseFollowUpGrid.defaultProps = {
  isLoading: false,
  clients: [],
  showExpenseAnalyst: false,
  showLineItemStatus: false,
  defaultDaysRange: null,
};

export default ExpenseFollowUpGrid;
